/**
 * Booking Store - Jotai atoms for booking state management
 *
 * Separates concerns:
 * - Filter state (UI state - pure Jotai)
 * - View mode (UI state)
 * - Booking data (server state - with TanStack Query)
 */

import { BookingAPI } from "@/services/BookingAPI";
import type {
  BookingViewMode,
  IBooking,
  IBookingFilters,
  IPostBooking,
  IServiceCustomer,
  IServiceEmployee,
  IServiceProject,
} from "@/services/types";
import { BookingType } from "@/services/types";
import { formatAPIDate } from "@/utils/date";
import { endOfWeek, startOfWeek } from "date-fns";
import { atom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import { atomWithMutation, atomWithQuery } from "jotai-tanstack-query";
import { selectModeAtom as selectionSelectModeAtom } from "./selectionStore";

// ============ BOOKING API INSTANCE ============
const bookingAPI = new BookingAPI();

// ============ FILTER STATE ATOMS ============

/**
 * Default filter state - current week with no entity filters
 */
const getDefaultFilters = (): IBookingFilters => {
  const today = new Date();
  return {
    employees: [],
    projects: [],
    customers: [],
    dateRange: {
      start: startOfWeek(today, { weekStartsOn: 1 }),
      end: endOfWeek(today, { weekStartsOn: 1 }),
    },
    bookingType: BookingType.HARD_BOOKING,
  };
};

/**
 * Main booking filters atom
 * Controls: date range, employees, projects, customers, booking type
 */
export const bookingFiltersAtom = atom<IBookingFilters>(getDefaultFilters());

/**
 * View mode atom - week, month, or year view
 * Persisted to localStorage for user preference retention
 */
export const bookingViewModeAtom = atomWithStorage<BookingViewMode>(
  "booking:viewMode",
  "week",
);

/**
 * Hide weekend toggle
 * Persisted to localStorage for user preference retention
 */
export const hideWeekendAtom = atomWithStorage<boolean>(
  "booking:hideWeekend",
  true,
);

/**
 * Booking type atom - hard or soft booking filter
 * Persisted to localStorage for user preference retention
 * This ensures the app stays in the selected booking type across page reloads
 */
export const bookingTypeAtom = atomWithStorage<
  typeof BookingType.HARD_BOOKING | typeof BookingType.SOFT_BOOKING
>("booking:bookingType", BookingType.HARD_BOOKING);

// ============ DERIVED FILTER ATOMS ============

/**
 * Derived: Check if any entity filters are active
 */
export const hasActiveFiltersAtom = atom((get) => {
  const filters = get(bookingFiltersAtom);
  return (
    filters.employees.length > 0 ||
    filters.projects.length > 0 ||
    filters.customers.length > 0
  );
});

/**
 * Derived: Convert filters to API query params
 */
export const bookingQueryParamsAtom = atom((get) => {
  const filters = get(bookingFiltersAtom);
  const params = new Map<string, string>();

  // Date range (always required)
  params.set("from_date", formatAPIDate(filters.dateRange.start));
  params.set("to_date", formatAPIDate(filters.dateRange.end));

  // Employee filter
  if (filters.employees.length > 0) {
    params.set("employee", filters.employees.map((e) => e.id).join(","));
  }

  // Project filter
  if (filters.projects.length > 0) {
    params.set("project", filters.projects.map((p) => p.id).join(","));
  }

  // Customer filter
  if (filters.customers.length > 0) {
    params.set("customer", filters.customers.map((c) => c.id).join(","));
  }

  // Booking type - always include ABSENCE with selected type
  const types = [filters.bookingType, BookingType.ABSENCE];
  params.set("b_type", [...new Set(types)].join(","));

  return params;
});

// ============ SERVER STATE ATOMS ============

/**
 * Bookings query atom - fetches bookings based on current filters
 * Uses jotai-tanstack-query for server state management
 */
export const bookingsQueryAtom = atomWithQuery((get) => {
  const filters = get(bookingFiltersAtom);
  const hasFilters = get(hasActiveFiltersAtom);

  return {
    queryKey: [
      "bookings",
      {
        from: formatAPIDate(filters.dateRange.start),
        to: formatAPIDate(filters.dateRange.end),
        employees: filters.employees.map((e) => e.id),
        projects: filters.projects.map((p) => p.id),
        customers: filters.customers.map((c) => c.id),
        bookingType: filters.bookingType,
      },
    ],
    queryFn: async () => {
      // Don't fetch if no entity filters - return empty array
      if (!hasFilters) {
        return [] as IBooking[];
      }

      const params = get(bookingQueryParamsAtom);
      return bookingAPI.getBookings(params);
    },
    staleTime: 2 * 60 * 1000, // 2 minutes - balance between freshness and request frequency
    enabled: hasFilters, // Only fetch when filters are set
  };
});

// ============ MUTATION ATOMS ============

/**
 * Create bookings mutation
 */
export const createBookingsMutationAtom = atomWithMutation(() => ({
  mutationKey: ["createBookings"],
  mutationFn: async (bookings: IPostBooking[]) => {
    return bookingAPI.createBookings(bookings);
  },
}));

/**
 * Update booking mutation
 */
export const updateBookingMutationAtom = atomWithMutation(() => ({
  mutationKey: ["updateBooking"],
  mutationFn: async (booking: IPostBooking) => {
    return bookingAPI.updateBooking(booking);
  },
}));

/**
 * Delete booking mutation
 */
export const deleteBookingMutationAtom = atomWithMutation(() => ({
  mutationKey: ["deleteBooking"],
  mutationFn: async (booking: IBooking) => {
    return bookingAPI.deleteBooking(booking);
  },
}));

// ============ DISPLAY STATE ATOMS ============

/**
 * Selected booking card data for detail sidebar view
 * When set, shows the yellow sidebar with booking details
 */
export interface SelectedCardData {
  id: string;
  date: Date;
  employee: IServiceEmployee | null;
  customer: IServiceCustomer | null;
  bookings: IBooking[];
  excludedBookings: IBooking[];
}

export const selectedCardAtom = atom<SelectedCardData | null>(null);

/**
 * Currently editing booking (for edit modal)
 */
export const editingBookingAtom = atom<IBooking | null>(null);

/**
 * Booking to delete (for confirmation dialog)
 */
export const deletingBookingAtom = atom<IBooking | null>(null);

// ============ UI STATE ATOMS ============

/**
 * Collapsed state for the left side panel on the booking page
 * Default: false (panel is open on page load)
 */
export const panelCollapsedAtom = atom<boolean>(false);

/**
 * Help modal visibility
 */
export const showHelpModalAtom = atom<boolean>(false);

/**
 * Derive the current panel variant from sidebar state
 */
export const panelVariantAtom = atom((get): "dark" | "accent" | "light" => {
  const selectedCard = get(selectedCardAtom);
  const selectMode = get(selectionSelectModeAtom);

  if (selectedCard) return "accent";
  if (selectMode) return "light";
  return "dark";
});

/**
 * Which view/content should be shown in the side panel
 */
export type PanelView = "filters" | "selectionTool" | "selectedCard";
export const panelViewAtom = atom<PanelView>((get) => {
  const selectedCard = get(selectedCardAtom);
  const selectMode = get(selectionSelectModeAtom);

  if (selectedCard) return "selectedCard";
  if (selectMode) return "selectionTool";
  return "filters";
});

// ============ FILTER ACTION HELPERS ============

/**
 * Create action atoms for filter updates
 * These are write-only atoms that update the filter state
 */

export const setDateRangeAtom = atom(
  null,
  (_get, set, dateRange: { start: Date; end: Date }) => {
    set(bookingFiltersAtom, (prev) => ({ ...prev, dateRange }));
  },
);

/**
 * Allows setters below to accept either a new array or an updater function.
 * Updaters receive the latest snapshot to avoid stale closure issues.
 */
type EntityUpdater<T> = T[] | ((previous: T[]) => T[]);

export const setEmployeesAtom = atom(
  null,
  (_get, set, employees: EntityUpdater<IServiceEmployee>) => {
    set(bookingFiltersAtom, (prev) => ({
      ...prev,
      employees:
        typeof employees === "function" ? employees(prev.employees) : employees,
    }));
  },
);

export const setProjectsAtom = atom(
  null,
  (_get, set, projects: EntityUpdater<IServiceProject>) => {
    set(bookingFiltersAtom, (prev) => ({
      ...prev,
      projects:
        typeof projects === "function" ? projects(prev.projects) : projects,
    }));
  },
);

export const setCustomersAtom = atom(
  null,
  (_get, set, customers: EntityUpdater<IServiceCustomer>) => {
    set(bookingFiltersAtom, (prev) => ({
      ...prev,
      customers:
        typeof customers === "function" ? customers(prev.customers) : customers,
    }));
  },
);

export const setBookingTypeAtom = atom(
  null,
  (
    _get,
    set,
    bookingType:
      | typeof BookingType.HARD_BOOKING
      | typeof BookingType.SOFT_BOOKING,
  ) => {
    // Update both the filter state and the persisted atom
    set(bookingFiltersAtom, (prev) => ({ ...prev, bookingType }));
    set(bookingTypeAtom, bookingType);
  },
);

export const clearFiltersAtom = atom(null, (_get, set) => {
  set(bookingFiltersAtom, (prev) => ({
    ...prev,
    employees: [],
    projects: [],
    customers: [],
  }));
});

export const resetFiltersAtom = atom(null, (_get, set) => {
  set(bookingFiltersAtom, getDefaultFilters());
  set(bookingViewModeAtom, "week");
  set(hideWeekendAtom, false);
  set(bookingTypeAtom, BookingType.HARD_BOOKING);
});
